import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Wrench, MapPin, Package } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import type { Equipment, EquipmentSupplier, SupplierAddress, Location } from '@/types/settings';

export default function EquipmentPage(): React.ReactElement {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        const [equipmentData, locationsData] = await Promise.all([
          dataService.getEquipment(),
          dataService.getLocations(),
        ]);
        setEquipment(equipmentData);
        setLocations(locationsData);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load equipment. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const [formData, setFormData] = useState<Partial<Equipment>>({
    code: '',
    description: '',
    boughtValue: 0,
    boughtDate: '',
    linkedAssetId: '',
    locationId: '',
    userText1: '',
    userText2: '',
    userText3: '',
    userText4: '',
    userText5: '',
    userText6: '',
    userValue1: 0,
    userValue2: 0,
    userValue3: 0,
    userDate1: '',
    userDate2: '',
    userDate3: '',
    userOption1: '',
    userOption2: '',
    userOption3: '',
    notes: '',
    supplier: {
      code: '',
      name: '',
      telephone: '',
      email: '',
      website: '',
      hirePurchase: false,
      invoiceReference: '',
      purchaseOrderReference: '',
      warrantyStartDate: '',
      warrantyFinishDate: '',
      warrantyCost: 0,
      addresses: [],
    },
  });

  const handleOpenDialog = (equip?: Equipment) => {
    if (equip) {
      setEditingEquipment(equip);
      setFormData(equip);
    } else {
      setEditingEquipment(null);
      setFormData({
        code: '',
        description: '',
        boughtValue: 0,
        boughtDate: '',
        linkedAssetId: '',
        locationId: '',
        notes: '',
        supplier: {
          code: '',
          name: '',
          telephone: '',
          email: '',
          website: '',
          hirePurchase: false,
          invoiceReference: '',
          purchaseOrderReference: '',
          warrantyStartDate: '',
          warrantyFinishDate: '',
          warrantyCost: 0,
          addresses: [],
        },
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingEquipment) {
        if (isDemoMode()) {
          setEquipment(
            equipment.map((eq) =>
              eq.id === editingEquipment.id
                ? { ...eq, ...formData, modifiedDate: new Date().toISOString() }
                : eq
            )
          );
        } else {
          const updated = await dataService.updateEquipment(editingEquipment.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as Equipment);
          setEquipment(equipment.map((eq) => (eq.id === updated.id ? updated : eq)));
        }
      } else {
        const newEquipment: Equipment = {
          id: String(equipment.length + 1),
          code: formData.code || '',
          description: formData.description,
          boughtValue: formData.boughtValue || 0,
          boughtDate: formData.boughtDate,
          linkedAssetId: formData.linkedAssetId,
          locationId: formData.locationId,
          userText1: formData.userText1,
          userText2: formData.userText2,
          userText3: formData.userText3,
          userText4: formData.userText4,
          userText5: formData.userText5,
          userText6: formData.userText6,
          userValue1: formData.userValue1,
          userValue2: formData.userValue2,
          userValue3: formData.userValue3,
          userDate1: formData.userDate1,
          userDate2: formData.userDate2,
          userDate3: formData.userDate3,
          userOption1: formData.userOption1,
          userOption2: formData.userOption2,
          userOption3: formData.userOption3,
          notes: formData.notes,
          supplier: formData.supplier,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        
        if (isDemoMode()) {
          setEquipment([...equipment, newEquipment]);
        } else {
          const created = await dataService.createEquipment(newEquipment);
          setEquipment([...equipment, created]);
        }
      }
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save equipment:', err);
      alert('Failed to save equipment. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this equipment?')) {
      try {
        if (isDemoMode()) {
          setEquipment(equipment.filter((eq) => eq.id !== id));
        } else {
          await dataService.deleteEquipment(id);
          setEquipment(equipment.filter((eq) => eq.id !== id));
        }
      } catch (err) {
        console.error('Failed to delete equipment:', err);
        alert('Failed to delete equipment. Please try again.');
      }
    }
  };

  const filteredEquipment = equipment.filter((eq) =>
    eq.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    eq.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getLocationName = (locationId?: string) => {
    const location = locations.find((loc) => loc.id === locationId);
    return location?.name || '-';
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Equipment</h2>
              <p className="text-muted-foreground">
                Manage equipment records and supplier information
              </p>
            </div>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Equipment
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Bought Value</TableHead>
              <TableHead>Bought Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Linked Asset</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  Loading equipment...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : filteredEquipment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No equipment found
                </TableCell>
              </TableRow>
            ) : (
              filteredEquipment.map((eq) => (
                <TableRow key={eq.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-muted-foreground" />
                      {eq.code}
                    </div>
                  </TableCell>
                  <TableCell>{eq.description || '-'}</TableCell>
                  <TableCell className="text-right">
                    ${eq.boughtValue.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {eq.boughtDate
                      ? new Date(eq.boughtDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {getLocationName(eq.locationId)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {eq.linkedAssetId ? (
                      <div className="flex items-center gap-1">
                        <Package className="h-3 w-3 text-muted-foreground" />
                        {eq.linkedAssetId}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(eq)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(eq.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEquipment ? 'Edit Equipment' : 'Add Equipment'}
            </DialogTitle>
            <DialogDescription>
              {editingEquipment
                ? 'Update the equipment details below.'
                : 'Enter the details for the new equipment.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="user-fields">User Fields</TabsTrigger>
              <TabsTrigger value="supplier">Supplier</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    placeholder="e.g., EQ-001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Equipment description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="boughtValue">Bought Value</Label>
                  <Input
                    id="boughtValue"
                    type="number"
                    step="0.01"
                    value={formData.boughtValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        boughtValue: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="boughtDate">Bought Date</Label>
                  <Input
                    id="boughtDate"
                    type="date"
                    value={formData.boughtDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, boughtDate: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedAsset">Linked Asset</Label>
                  <Input
                    id="linkedAsset"
                    value={formData.linkedAssetId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, linkedAssetId: e.target.value })
                    }
                    placeholder="Asset ID"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Select
                    value={formData.locationId}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, locationId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* User Fields Tab */}
            <TabsContent value="user-fields" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Text Fields</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <div key={num} className="space-y-2">
                      <Label htmlFor={`userText${num}`}>User Text {num}</Label>
                      <Input
                        id={`userText${num}`}
                        value={formData[`userText${num}` as keyof Equipment] as string}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            [`userText${num}`]: e.target.value,
                          })
                        }
                        placeholder={`Text field ${num}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Value Fields</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="space-y-2">
                      <Label htmlFor={`userValue${num}`}>User Value {num}</Label>
                      <Input
                        id={`userValue${num}`}
                        type="number"
                        value={formData[`userValue${num}` as keyof Equipment] as number}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            [`userValue${num}`]: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder={`Value ${num}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Date Fields</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="space-y-2">
                      <Label htmlFor={`userDate${num}`}>User Date {num}</Label>
                      <Input
                        id={`userDate${num}`}
                        type="date"
                        value={formData[`userDate${num}` as keyof Equipment] as string}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            [`userDate${num}`]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Option Fields</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="space-y-2">
                      <Label htmlFor={`userOption${num}`}>User Option {num}</Label>
                      <Input
                        id={`userOption${num}`}
                        value={formData[`userOption${num}` as keyof Equipment] as string}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          setFormData({
                            ...formData,
                            [`userOption${num}`]: e.target.value,
                          })
                        }
                        placeholder={`Option ${num}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Supplier Tab */}
            <TabsContent value="supplier" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Supplier Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="supplierCode">Supplier Code</Label>
                    <Input
                      id="supplierCode"
                      value={formData.supplier?.code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, code: e.target.value },
                        })
                      }
                      placeholder="SUP-001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="supplierName">Supplier Name</Label>
                    <Input
                      id="supplierName"
                      value={formData.supplier?.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, name: e.target.value },
                        })
                      }
                      placeholder="Supplier name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telephone">Telephone</Label>
                    <Input
                      id="telephone"
                      value={formData.supplier?.telephone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, telephone: e.target.value },
                        })
                      }
                      placeholder="+1-555-0123"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.supplier?.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, email: e.target.value },
                        })
                      }
                      placeholder="supplier@example.com"
                    />
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.supplier?.website}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, website: e.target.value },
                        })
                      }
                      placeholder="https://www.supplier.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invoiceRef">Invoice Reference</Label>
                    <Input
                      id="invoiceRef"
                      value={formData.supplier?.invoiceReference}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: {
                            ...formData.supplier!,
                            invoiceReference: e.target.value,
                          },
                        })
                      }
                      placeholder="INV-2024-001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="poRef">Purchase Order Reference</Label>
                    <Input
                      id="poRef"
                      value={formData.supplier?.purchaseOrderReference}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: {
                            ...formData.supplier!,
                            purchaseOrderReference: e.target.value,
                          },
                        })
                      }
                      placeholder="PO-2024-001"
                    />
                  </div>

                  <div className="flex items-center space-x-2 col-span-2">
                    <Switch
                      id="hirePurchase"
                      checked={formData.supplier?.hirePurchase}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          supplier: { ...formData.supplier!, hirePurchase: checked },
                        })
                      }
                    />
                    <Label htmlFor="hirePurchase" className="cursor-pointer">
                      Hire Purchase
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Warranty Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="warrantyStart">Warranty Start Date</Label>
                    <Input
                      id="warrantyStart"
                      type="date"
                      value={formData.supplier?.warrantyStartDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: {
                            ...formData.supplier!,
                            warrantyStartDate: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="warrantyFinish">Warranty Finish Date</Label>
                    <Input
                      id="warrantyFinish"
                      type="date"
                      value={formData.supplier?.warrantyFinishDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: {
                            ...formData.supplier!,
                            warrantyFinishDate: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="warrantyCost">Warranty Cost</Label>
                    <Input
                      id="warrantyCost"
                      type="number"
                      step="0.01"
                      value={formData.supplier?.warrantyCost}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          supplier: {
                            ...formData.supplier!,
                            warrantyCost: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Additional notes about this equipment"
                  rows={10}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.code}>
              {editingEquipment ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
